import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWeb3V3 } from '../contexts/Web3ContextV3';

/**
 * V3 Home Page - Black & White Premium Theme
 * Project Introduction + Real-time Data Display
 */
const HomeV3 = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    account,
    systemConfig,
    nftStats,
    balances,
    connectWallet,
    isConnecting
  } = useWeb3V3();

  // Real-time updated data state
  const [stats, setStats] = useState({
    totalSupply: '100000',
    totalMinted: '0',
    prizePoolBalance: '0',
    currentDrawId: '0',
    usdtPrice: '0',
    ethPrice: '0'
  });

  // Countdown state
  const [countdown, setCountdown] = useState('');

  useEffect(() => {
    if (nftStats && systemConfig) {
      const totalMinted = parseInt(nftStats.totalMinted || '0');
      const totalSupply = parseInt(nftStats.totalSupply || '100000');
      const prizePoolBalance = parseFloat(nftStats.prizePoolBalance || '0');

      setStats({
        totalSupply: totalSupply.toLocaleString(),
        totalMinted: totalMinted.toLocaleString(),
        prizePoolBalance: prizePoolBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        currentDrawId: nftStats.currentDrawId || '0',
        usdtPrice: systemConfig.usdtPrice || '0',
        ethPrice: systemConfig.ethPrice || '0'
      });
    }
  }, [nftStats, systemConfig]);

  // Countdown calculation - using lastDrawTime (nftStats) + drawInterval (systemConfig) to calculate next draw time
  useEffect(() => {
    const updateCountdown = () => {
      // lastDrawTime is in nftStats, drawInterval is in systemConfig
      const lastDraw = parseInt(nftStats?.lastDrawTime || '0');
      const drawIntervalSec = parseInt(systemConfig?.drawInterval || '0');

      // If data not loaded or drawInterval is 0, do not show countdown
      if (drawIntervalSec === 0) {
        setCountdown('--:--:--');
        return;
      }

      // If lastDrawTime is 0, means never drawn
      if (lastDraw === 0) {
        setCountdown(t('home.drawing'));
        return;
      }

      const now = Math.floor(Date.now() / 1000);
      const nextDraw = lastDraw + drawIntervalSec;
      const diff = nextDraw - now;

      if (diff > 0) {
        const hours = Math.floor(diff / 3600);
        const minutes = Math.floor((diff % 3600) / 60);
        const seconds = diff % 60;
        setCountdown(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
      } else {
        setCountdown(t('home.drawing'));
      }
    };

    // Execute immediately once
    updateCountdown();

    // Update every second
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [nftStats, systemConfig, t]);

  // Navigate to minting page
  const handleMintClick = () => {
    if (!account) {
      connectWallet();
    } else {
      navigate('/mint');
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero section */}
      <section className="relative container-v3 pt-20 pb-32">
        {/* Background grid effect */}
        <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none"></div>

        <div className="relative z-10">
          {/* Main title */}
          <div className="text-center mb-16">
            <h1 className="text-6xl md:text-8xl font-black mb-6 tracking-tight">
              {t('home.title')}
            </h1>
            <p className="text-xl md:text-2xl text-gray-400 max-w-3xl mx-auto">
              {t('home.subtitle')}
            </p>
          </div>

          {/* Core data display */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-12">
            {/* Total supply */}
            <div className="card p-6 text-center">
              <div className="text-gray-500 text-sm mb-2 uppercase tracking-wider">{t('home.totalSupply')}</div>
              <div className="text-4xl font-bold font-mono">{stats.totalSupply}</div>
              <div className="text-gray-600 text-xs mt-2">{t('common.nfts')}</div>
            </div>

            {/* Minted */}
            <div className="card p-6 text-center">
              <div className="text-gray-500 text-sm mb-2 uppercase tracking-wider">{t('home.sold')}</div>
              <div className="text-4xl font-bold font-mono text-white">{stats.totalMinted}</div>
              <div className="text-gray-600 text-xs mt-2">
                {stats.totalSupply !== '0' &&
                  `${((parseInt(stats.totalMinted.replace(/,/g, '')) / parseInt(stats.totalSupply.replace(/,/g, ''))) * 100).toFixed(1)}%`
                }
              </div>
            </div>

            {/* Prize Pool */}
            <div className="card-glow p-6 text-center">
              <div className="text-gray-500 text-sm mb-2 uppercase tracking-wider">{t('home.prizePool')}</div>
              <div className="text-4xl font-bold font-mono text-yellow-400">${stats.prizePoolBalance}</div>
              <div className="text-gray-600 text-xs mt-2">{t('home.prizePoolDesc')}</div>
            </div>

            {/* Current draw period */}
            <div className="card p-6 text-center">
              <div className="text-gray-500 text-sm mb-2 uppercase tracking-wider">{t('home.currentDraw')}</div>
              <div className="text-4xl font-bold font-mono">#{stats.currentDrawId}</div>
              <div className="text-gray-600 text-xs mt-2">{t('common.drawId')}</div>
            </div>
          </div>

          {/* Price information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12 max-w-2xl mx-auto">
            {/* USDT price */}
            {systemConfig.usdtEnabled && (
              <div className="card p-6">
                <div className="text-gray-500 text-sm mb-1">{t('home.usdtPrice')}</div>
                <div className="text-3xl font-bold font-mono">{stats.usdtPrice}</div>
              </div>
            )}

            {/* ETH price */}
            {systemConfig.ethEnabled && (
              <div className="card p-6">
                <div className="text-gray-500 text-sm mb-1">{t('home.ethPrice')}</div>
                <div className="text-3xl font-bold font-mono">{stats.ethPrice}</div>
              </div>
            )}
          </div>

          {/* CTA button */}
          <div className="text-center">
            <button
              onClick={handleMintClick}
              disabled={isConnecting}
              className="btn-primary px-12 py-4 text-xl font-bold hover:scale-105 transition-transform"
            >
              {isConnecting ? t('common.connecting') : account ? t('home.mintNow') : t('home.connectToStart')}
            </button>

            {/* Countdown */}
            {countdown && (
              <div className="mt-6 text-gray-500">
                <span className="text-sm uppercase tracking-wider">{t('home.nextDraw')}：</span>
                <span className="ml-2 text-xl font-mono text-white">{countdown}</span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Features introduction */}
      <section className="bg-gray-950 py-20">
        <div className="container-v3">
          <h2 className="text-4xl font-bold text-center mb-16">{t('home.features.title')}</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature 1: Fair and transparent */}
            <div className="card p-8 hover:border-white transition-all">
              <h3 className="text-2xl font-bold mb-4">{t('home.features.fairness.title')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('home.features.fairness.desc')}
              </p>
            </div>

            {/* Feature 2: Dual-currency payment */}
            <div className="card p-8 hover:border-white transition-all">
              <h3 className="text-2xl font-bold mb-4">{t('home.features.payment.title')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('home.features.payment.desc')}
              </p>
            </div>

            {/* Feature 3: Daily dividends */}
            <div className="card p-8 hover:border-white transition-all">
              <h3 className="text-2xl font-bold mb-4">{t('home.features.dividend.title')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('home.features.dividend.desc')}
              </p>
            </div>

            {/* Feature 4: Multi-tier prizes */}
            <div className="card p-8 hover:border-white transition-all">
              <h3 className="text-2xl font-bold mb-4">{t('home.features.prizes.title')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('home.features.prizes.desc')}
              </p>
            </div>

            {/* Feature 5: Personal signature */}
            <div className="card p-8 hover:border-white transition-all">
              <h3 className="text-2xl font-bold mb-4">{t('home.features.signature.title')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('home.features.signature.desc')}
              </p>
            </div>

            {/* Feature 6: P2P trading */}
            <div className="card p-8 hover:border-white transition-all">
              <h3 className="text-2xl font-bold mb-4">{t('home.features.trading.title')}</h3>
              <p className="text-gray-400 leading-relaxed">
                {t('home.features.trading.desc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How to participate */}
      <section className="py-20">
        <div className="container-v3">
          <h2 className="text-4xl font-bold text-center mb-16">{t('home.howTo.title')}</h2>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-6">
              {/* Step 1 */}
              <div className="card p-6 flex items-start gap-6">
                <div className="flex-shrink-0 w-12 h-12 bg-white text-black flex items-center justify-center text-2xl font-bold">
                  1
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">{t('home.howTo.step1.title')}</h3>
                  <p className="text-gray-400">
                    {t('home.howTo.step1.desc')}
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="card p-6 flex items-start gap-6">
                <div className="flex-shrink-0 w-12 h-12 bg-white text-black flex items-center justify-center text-2xl font-bold">
                  2
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">{t('home.howTo.step2.title')}</h3>
                  <p className="text-gray-400">
                    {t('home.howTo.step2.desc')}
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="card p-6 flex items-start gap-6">
                <div className="flex-shrink-0 w-12 h-12 bg-white text-black flex items-center justify-center text-2xl font-bold">
                  3
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">{t('home.howTo.step3.title')}</h3>
                  <p className="text-gray-400">
                    {t('home.howTo.step3.desc')}
                  </p>
                </div>
              </div>

              {/* Step 4 */}
              <div className="card p-6 flex items-start gap-6">
                <div className="flex-shrink-0 w-12 h-12 bg-white text-black flex items-center justify-center text-2xl font-bold">
                  4
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">{t('home.howTo.step4.title')}</h3>
                  <p className="text-gray-400">
                    {t('home.howTo.step4.desc')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Data statistics */}
      {account && balances && (
        <section className="bg-gray-950 py-12">
          <div className="container-v3">
            <div className="card p-8">
              <h3 className="text-2xl font-bold mb-6">{t('home.myAccount')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <div className="text-gray-500 text-sm mb-1">{t('common.walletAddress')}</div>
                  <div className="font-mono text-sm">
                    {account.slice(0, 6)}...{account.slice(-4)}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 text-sm mb-1">{t('home.usdtBalance')}</div>
                  <div className="text-2xl font-bold font-mono">{balances.usdt}</div>
                </div>
                <div>
                  <div className="text-gray-500 text-sm mb-1">{t('home.ethBalance')}</div>
                  <div className="text-2xl font-bold font-mono">{balances.eth}</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Footer CTA */}
      <section className="py-20 text-center">
        <div className="container-v3">
          <h2 className="text-5xl font-bold mb-8">{t('home.cta.title')}</h2>
          <p className="text-xl text-gray-400 mb-12 max-w-2xl mx-auto">
            {t('home.cta.subtitle')}
          </p>
          <button
            onClick={handleMintClick}
            disabled={isConnecting}
            className="btn-primary px-16 py-5 text-2xl font-bold hover:scale-105 transition-transform"
          >
            {account ? t('home.cta.mintNow') : t('common.connectWallet')}
          </button>
        </div>
      </section>
    </div>
  );
};

export default HomeV3;
